import { useState } from 'react';
import { trpc } from '@/lib/trpc';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

type SessionFeeling = 'crushed_it' | 'solid' | 'tough' | 'cooked';

interface SessionStats {
  totalSets: number;
  totalVolumeLbs: number;
  durationSeconds: number;
}

interface PostSessionFeedbackCardProps {
  sessionId: string;
  sessionName: string;
  stats?: SessionStats;
  skippedExercises?: Array<{ name: string; reason: string }>;
  onDone: (feeling: SessionFeeling, nudge: string) => void;
  onSkip: () => void;
}

const FEELINGS: Array<{
  value: SessionFeeling;
  emoji: string;
  label: string;
  sublabel: string;
  color: string;
  border: string;
  bg: string;
}> = [
  {
    value: 'crushed_it',
    emoji: '🔥',
    label: 'CRUSHED IT',
    sublabel: 'Felt strong, hit PRs',
    color: 'text-orange-400',
    border: 'border-orange-400/60',
    bg: 'bg-orange-400/10 hover:bg-orange-400/20',
  },
  {
    value: 'solid',
    emoji: '✅',
    label: 'SOLID',
    sublabel: 'Good session, on track',
    color: 'text-emerald-400',
    border: 'border-emerald-400/60',
    bg: 'bg-emerald-400/10 hover:bg-emerald-400/20',
  },
  {
    value: 'tough',
    emoji: '😤',
    label: 'TOUGH',
    sublabel: 'Got through it, felt heavy',
    color: 'text-yellow-400',
    border: 'border-yellow-400/60',
    bg: 'bg-yellow-400/10 hover:bg-yellow-400/20',
  },
  {
    value: 'cooked',
    emoji: '💀',
    label: 'COOKED',
    sublabel: 'Drained, need recovery',
    color: 'text-red-400',
    border: 'border-red-400/60',
    bg: 'bg-red-400/10 hover:bg-red-400/20',
  },
];

function formatDuration(secs: number): string {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function formatVolume(lbs: number): string {
  if (lbs >= 1000) return `${(lbs / 1000).toFixed(1)}K`;
  return lbs.toLocaleString();
}

export function PostSessionFeedbackCard({
  sessionId,
  sessionName,
  stats,
  skippedExercises = [],
  onDone,
  onSkip,
}: PostSessionFeedbackCardProps) {
  const [selected, setSelected] = useState<SessionFeeling | null>(null);
  const [nudge, setNudge] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [celebrated, setCelebrated] = useState(false);

  const submitFeedback = trpc.sessions.submitFeedback.useMutation({
    onSuccess: (data) => {
      setNudge(data.nudge);
      setSubmitted(true);
    },
    onError: () => {
      toast.error('Failed to save feedback');
    },
  });

  const handleSelect = (feeling: SessionFeeling) => {
    setSelected(feeling);
    submitFeedback.mutate({ sessionId, feeling });
  };

  const selectedFeeling = FEELINGS.find((f) => f.value === selected);

  // ── Celebration screen (after nudge is shown, user taps "DONE") ──────────
  if (celebrated) {
    return (
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/90 backdrop-blur-sm">
        <div className="w-full max-w-md mx-auto bg-[#0a0a0a] border border-emerald-500/30 rounded-t-2xl sm:rounded-2xl p-8 pb-10 sm:pb-8 shadow-2xl text-center">
          <div className="text-5xl mb-4">🏆</div>
          <div className="font-mono text-[10px] text-emerald-400 uppercase tracking-widest mb-2">SESSION SAVED</div>
          <h2 className="font-mono text-xl font-bold text-foreground mb-1">{sessionName}</h2>

          {stats && (
            <div className="grid grid-cols-3 gap-3 my-6">
              <div className="bg-zinc-900/80 border border-zinc-800 rounded-xl p-3">
                <div className="font-mono text-2xl font-bold text-primary">{stats.totalSets}</div>
                <div className="font-mono text-[9px] text-muted-foreground uppercase mt-1">SETS</div>
              </div>
              <div className="bg-zinc-900/80 border border-zinc-800 rounded-xl p-3">
                <div className="font-mono text-2xl font-bold text-primary">{formatVolume(stats.totalVolumeLbs)}</div>
                <div className="font-mono text-[9px] text-muted-foreground uppercase mt-1">LBS</div>
              </div>
              <div className="bg-zinc-900/80 border border-zinc-800 rounded-xl p-3">
                <div className="font-mono text-2xl font-bold text-primary">{formatDuration(stats.durationSeconds)}</div>
                <div className="font-mono text-[9px] text-muted-foreground uppercase mt-1">TIME</div>
              </div>
            </div>
          )}

          {skippedExercises.length > 0 && (
            <div className="mt-4 mb-2 text-left">
              <div className="text-[9px] font-mono text-orange-400/70 uppercase tracking-widest mb-1.5">⚡ SKIPPED THIS SESSION</div>
              <div className="space-y-1">
                {skippedExercises.map((ex, i) => (
                  <div key={i} className="flex items-center gap-2 text-[10px] font-mono text-orange-400/80">
                    <span className="text-orange-500/60">—</span>
                    <span className="font-bold">{ex.name}</span>
                    <span className="text-orange-400/50">{ex.reason}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className={`flex items-center justify-center gap-2 mb-6 ${selectedFeeling?.color}`}>
            <span className="text-xl">{selectedFeeling?.emoji}</span>
            <span className="font-mono text-sm font-bold uppercase tracking-wider">{selectedFeeling?.label}</span>
          </div>

          <Button
            size="lg"
            className="w-full font-mono text-sm bg-primary text-primary-foreground hover:bg-primary/90 h-12"
            onClick={() => onDone(selected!, nudge ?? '')}
          >
            BACK TO COMMAND
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/80 backdrop-blur-sm">
      <div className="w-full max-w-md mx-auto bg-[#0a0a0a] border border-border/40 rounded-t-2xl sm:rounded-2xl p-6 pb-8 sm:pb-6 shadow-2xl">

        {/* Session summary stats */}
        {stats && (
          <div className="grid grid-cols-3 gap-2 mb-5">
            <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-2.5 text-center">
              <div className="font-mono text-xl font-bold text-foreground">{stats.totalSets}</div>
              <div className="font-mono text-[9px] text-muted-foreground uppercase mt-0.5">SETS</div>
            </div>
            <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-2.5 text-center">
              <div className="font-mono text-xl font-bold text-foreground">{formatVolume(stats.totalVolumeLbs)}</div>
              <div className="font-mono text-[9px] text-muted-foreground uppercase mt-0.5">LBS</div>
            </div>
            <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-2.5 text-center">
              <div className="font-mono text-xl font-bold text-foreground">{formatDuration(stats.durationSeconds)}</div>
              <div className="font-mono text-[9px] text-muted-foreground uppercase mt-0.5">TIME</div>
            </div>
          </div>
        )}

        {/* Skipped exercises notice */}
        {skippedExercises.length > 0 && (
          <div className="mb-4 p-3 bg-orange-500/5 border border-orange-500/20 rounded-sm">
            <div className="text-[9px] font-mono text-orange-400/70 uppercase tracking-widest mb-2">⚡ SKIPPED THIS SESSION</div>
            <div className="space-y-1">
              {skippedExercises.map((ex, i) => (
                <div key={i} className="flex items-center gap-2 text-[10px] font-mono">
                  <span className="text-orange-500/60">—</span>
                  <span className="text-orange-300 font-bold">{ex.name}</span>
                  <span className="text-orange-400/50">{ex.reason}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Header */}
        <div className="text-center mb-5">
          <div className="font-mono text-[10px] text-muted-foreground uppercase tracking-widest mb-1">
            SESSION COMPLETE
          </div>
          <h2 className="font-mono text-lg font-bold text-foreground truncate">{sessionName}</h2>
          <p className="text-sm text-muted-foreground mt-1 font-mono">How did that feel?</p>
        </div>

        {!submitted ? (
          <>
            {/* Feeling grid */}
            <div className="grid grid-cols-2 gap-3 mb-6">
              {FEELINGS.map((f) => (
                <button
                  key={f.value}
                  onClick={() => handleSelect(f.value)}
                  disabled={submitFeedback.isPending}
                  className={`
                    flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all duration-150
                    active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed
                    ${f.bg} ${f.border}
                    ${selected === f.value ? 'ring-2 ring-offset-2 ring-offset-[#0a0a0a] ring-primary' : ''}
                  `}
                >
                  <span className="text-3xl leading-none">{f.emoji}</span>
                  <span className={`font-mono text-xs font-bold uppercase tracking-wider ${f.color}`}>
                    {f.label}
                  </span>
                  <span className="text-[10px] text-muted-foreground text-center leading-tight">
                    {f.sublabel}
                  </span>
                </button>
              ))}
            </div>

            {/* Skip */}
            <button
              onClick={onSkip}
              className="w-full text-center text-xs text-muted-foreground hover:text-foreground transition-colors font-mono py-2"
            >
              SKIP
            </button>
          </>
        ) : (
          <>
            {/* Nudge display */}
            <div className="mb-6">
              <div className={`flex items-center gap-2 mb-3 ${selectedFeeling?.color}`}>
                <span className="text-2xl">{selectedFeeling?.emoji}</span>
                <span className="font-mono text-sm font-bold uppercase tracking-wider">
                  {selectedFeeling?.label}
                </span>
              </div>

              {nudge ? (
                <div className="forge-panel p-4 rounded-xl border border-primary/20 bg-primary/5">
                  <div className="font-mono text-[9px] text-primary uppercase tracking-widest mb-2">
                    FORGE INSIGHT
                  </div>
                  <p className="text-sm text-foreground leading-relaxed">{nudge}</p>
                </div>
              ) : (
                <div className="forge-panel p-4 rounded-xl border border-border/20 bg-secondary/20">
                  <p className="text-sm text-muted-foreground">Feedback logged.</p>
                </div>
              )}
            </div>

            {/* Action buttons */}
            <div className="flex flex-col gap-2">
              {nudge?.includes('Hypertrophy') && (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full font-mono text-xs border-purple-400/40 text-purple-400 hover:bg-purple-400/10"
                  onClick={() => {
                    toast.info('Switch to Hypertrophy mode in your next session builder.');
                    setCelebrated(true);
                  }}
                >
                  SWITCH TO HYPERTROPHY MODE
                </Button>
              )}
              {(nudge?.includes('deload') || nudge?.includes('Deload')) && (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full font-mono text-xs border-blue-400/40 text-blue-400 hover:bg-blue-400/10"
                  onClick={() => {
                    toast.info('Schedule a deload week in your mesocycle planner.');
                    setCelebrated(true);
                  }}
                >
                  SCHEDULE DELOAD WEEK
                </Button>
              )}
              <Button
                size="sm"
                className="w-full font-mono text-xs bg-primary text-primary-foreground hover:bg-primary/90"
                onClick={() => setCelebrated(true)}
              >
                SESSION SAVED →
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
